// src/pages/index.js

import {
  Box,
  Button,
  Container,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSwipeable } from "react-swipeable";

import Footer from "../_global/Footer";
import Header from "../_global/Header";
import NavTabs from "../_global/NavTabs";
import CreditProfile from "./CreditProfile";
import SpecialOffers from "./SpecialOffers";

const TABS = (isMapsReady) => [
  { path: "/credit-profile", element: <CreditProfile /> },
  { path: "/offers", element: <SpecialOffers isMapsReady={isMapsReady} /> },
];

const variants = {
  enter: (direction) => ({
    x: direction > 0 ? window.innerWidth : -window.innerWidth,
    opacity: 0,
  }),
  center: {
    x: 0,
    opacity: 1,
  },
  exit: (direction) => ({
    x: direction < 0 ? window.innerWidth : -window.innerWidth,
    opacity: 0,
  }),
};

export default function Home({ isMapsReady }) {
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [isLoaded, setIsLoaded] = useState(false);
  const [swipeDir, setSwipeDir] = useState(0);
  const [showSwipeHint, setShowSwipeHint] = useState(false);

  // footer only after full page load
  useEffect(() => {
    if (document.readyState === "complete") {
      setIsLoaded(true);
    } else {
      const onLoad = () => setIsLoaded(true);
      window.addEventListener("load", onLoad);
      return () => window.removeEventListener("load", onLoad);
    }
  }, []);

  // show swipe hint once per session (or always in non-prod), only on mobile
  useEffect(() => {
    if (!isMobile) return;

    const env = process.env.REACT_APP_ENVIRONMENT || "development";
    // const forceShow = env !== "production";
    const forceShow = false;
    if (forceShow) {
      setShowSwipeHint(true);
    } else {
      const shown = sessionStorage.getItem("swipeHintShown") === "true";
      if (!shown) setShowSwipeHint(true);
    }
  }, [isMobile]);

  const dismissSwipeHint = () => {
    setShowSwipeHint(false);
    sessionStorage.setItem("swipeHintShown", "true");
  };

  // figure out which tab we’re on
  const tabs = TABS(isMapsReady);
  const currentIndex = Math.max(
    0,
    tabs.findIndex((t) => location.pathname.startsWith(t.path))
  );

  // swipe handlers (mobile only)
  const swipeHandlers = useSwipeable({
    trackTouch: true,
    trackMouse: false,
    delta: 50,
    swipeDuration: 300,
    onSwipedLeft: () => {
      if (currentIndex < tabs.length - 1) {
        setSwipeDir(1);
        navigate(tabs[currentIndex + 1].path);
      }
    },
    onSwipedRight: () => {
      if (currentIndex > 0) {
        setSwipeDir(-1);
        navigate(tabs[currentIndex - 1].path);
      }
    },
  });

  // pick content
  const renderContent = useMemo(() => {
    const tab = tabs.find((t) => location.pathname.startsWith(t.path));
    return tab?.element ?? <CreditProfile />;
  }, [location.pathname, tabs]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
        // mobile: tiled background; desktop: transparent
        backgroundImage: {
          xs: `url('/assets/app-backgrounds/bg15-01.jpg')`,
          md: "none",
        },
        backgroundRepeat: {
          xs: "no-repeat",
          md: "no-repeat",
        },
        backgroundSize: {
          xs: "cover",
          md: "initial",
        },
        backgroundPosition: {
          xs: "center",
          md: "none",
        },
        backgroundAttachment: {
          xs: "fixed",
          md: "scroll",
        },
        // preserve transparent on desktop
        backgroundColor: {
          xs: "transparent",
          md: "transparent",
        },
      }}
    >
      <Header />

      {/* always show NavTabs on mobile */}
      <Box
        sx={{ display: { xs: "flex", md: "none" }, justifyContent: "center" }}
      >
        <NavTabs />
      </Box>

      {/* swipe + main content */}
      <Container
        {...(isMobile ? swipeHandlers : {})}
        sx={{
          flex: "1 0 auto",
          touchAction: isMobile ? "pan-y" : "auto",
          position: "relative",
          overflow: isMobile ? "hidden" : "visible",
          p: 0,
        }}
      >
        {isMobile ? (
          <AnimatePresence initial={false} custom={swipeDir}>
            <motion.div
              key={location.pathname}
              custom={swipeDir}
              variants={variants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.3 }}
              style={{ width: "100%" }}
            >
              {renderContent}
            </motion.div>
          </AnimatePresence>
        ) : (
          renderContent
        )}
      </Container>

      {/* one-time swipe hint */}
      {isMobile && showSwipeHint && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            bgcolor: "rgba(0,0,0,0.8)",
            zIndex: 2000,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            px: 2,
          }}
        >
          <Box
            component="img"
            src="/assets/swipe-animation.gif"
            alt="Swipe to navigate"
            sx={{
              width: "60%",
              maxWidth: 120,
              opacity: 0.95,
              mb: 2,
              borderRadius: "100%",
            }}
          />
          <Typography variant="body1" sx={{ color: "#fff", mb: 2 }}>
            Swipe left or right to navigate between tabs
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={dismissSwipeHint}
            sx={{ px: 4, py: 1, boxShadow: "none", color: "#fff" }}
          >
            OK
          </Button>
        </Box>
      )}

      {isLoaded && <Footer />}
    </Box>
  );
}
